/**
 * <product-thumbnail-rail>
 *
 * Sticky PDP thumbnail rail. Renders one <button> per media item; clicking a
 * thumbnail scrolls the matching gallery tile into view, and the active state
 * follows the gallery as it scrolls (via IntersectionObserver, no scroll
 * listeners). Keyboard: Arrow keys move + activate, Home/End jump to ends.
 *
 * The element itself (`.pdp-thumb-rail`) is the internal scroll container, so
 * keeping the active thumbnail in view only scrolls the rail, never the page.
 */
class ProductThumbnailRail extends HTMLElement {
  #controller = new AbortController();
  #io = null;
  #mutationObserver = null;
  #mql = window.matchMedia('(min-width: 1200px)');
  /** @type {HTMLButtonElement[]} */
  #buttons = [];
  /** @type {HTMLElement | null} */
  #scrollContainer = null;
  /** @type {Set<Element>} */
  #visible = new Set();

  connectedCallback() {
    this.#buttons = Array.from(this.querySelectorAll('[data-thumb]'));
    if (this.#buttons.length === 0) return;

    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    // Re-wire when the gallery is swapped out (e.g. on a variant update).
    const layout = this.parentElement;
    if (layout) {
      this.#mutationObserver = new MutationObserver(() => this.#setup());
      this.#mutationObserver.observe(layout, { childList: true });
    }

    // Re-evaluate the observer when we cross the rail breakpoint.
    this.#mql.addEventListener('change', this.#setup, { signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#io?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** (Re)locate the gallery scroll container + tiles and attach the observer. */
  #setup = () => {
    this.#io?.disconnect();
    this.#io = null;
    this.#visible.clear();

    const layout = this.parentElement;
    this.#scrollContainer = layout?.querySelector('.media-gallery__grid[data-gallery-scroll]') ?? null;

    // Only observe when the rail layout is active and the gallery is present.
    if (!this.#scrollContainer || !this.#mql.matches) return;

    const tiles = this.#buttons
      .map((btn) => document.getElementById(btn.dataset.mediaTarget ?? ''))
      .filter((el) => el instanceof HTMLElement);

    if (tiles.length === 0) return;

    this.#io = new IntersectionObserver(this.#handleIntersect, {
      root: this.#scrollContainer,
      rootMargin: '-45% 0px -45% 0px',
      threshold: 0,
    });

    for (const tile of tiles) this.#io.observe(tile);
  };

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    // Track which tiles currently sit in the centre band.
    for (const entry of entries) {
      if (entry.isIntersecting) this.#visible.add(entry.target);
      else this.#visible.delete(entry.target);
    }

    if (this.#visible.size === 0) return;

    // In a 2-up grid several tiles share the centre band; pick the topmost,
    // left-most one so exactly one thumbnail is highlighted, without jitter.
    let best = null;
    for (const tile of this.#visible) {
      if (!(tile instanceof HTMLElement)) continue;
      if (!best) {
        best = tile;
        continue;
      }
      const a = tile.getBoundingClientRect();
      const b = best.getBoundingClientRect();
      if (a.top < b.top - 1 || (Math.abs(a.top - b.top) <= 1 && a.left < b.left)) {
        best = tile;
      }
    }

    if (best) this.#setActive(best.id, { scrollRail: true });
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest('[data-thumb]');
    if (!(btn instanceof HTMLButtonElement)) return;

    this.#activate(btn);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest('[data-thumb]');
    if (!(btn instanceof HTMLButtonElement)) return;

    const index = this.#buttons.indexOf(btn);
    if (index === -1) return;

    let nextIndex = null;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = Math.min(index + 1, this.#buttons.length - 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = Math.max(index - 1, 0);
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#buttons.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const next = this.#buttons[nextIndex];
    if (!next) return;
    next.focus();
    this.#activate(next);
  };

  /**
   * Scroll the matching gallery tile into view and mark the thumbnail active.
   * @param {HTMLButtonElement} btn
   */
  #activate(btn) {
    const targetId = btn.dataset.mediaTarget;
    if (!targetId) return;

    const tile = document.getElementById(targetId);
    tile?.scrollIntoView({ behavior: this.#behavior, block: 'start' });

    this.#setActive(targetId, { scrollRail: false });
  }

  /**
   * Set aria-current on the matching thumbnail and its 2-up row partner.
   * @param {string} targetId
   * @param {{ scrollRail: boolean }} [options]
   */
  #setActive(targetId, { scrollRail = false } = {}) {
    const btn = this.#buttons.find((b) => b.dataset.mediaTarget === targetId);
    if (!btn) return;

    for (const b of this.#buttons) b.removeAttribute('aria-current');
    btn.setAttribute('aria-current', 'true');

    if (scrollRail) this.#scrollThumbIntoView(btn);
  }

  /**
   * Keep the active thumbnail within the rail's visible area — scrolling the
   * rail only, never the page.
   * @param {HTMLButtonElement} btn
   */
  #scrollThumbIntoView(btn) {
    const railRect = this.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const pad = 8;

    if (btnRect.top < railRect.top) {
      this.scrollTop -= railRect.top - btnRect.top + pad;
    } else if (btnRect.bottom > railRect.bottom) {
      this.scrollTop += btnRect.bottom - railRect.bottom + pad;
    }
  }
}

if (!customElements.get('product-thumbnail-rail')) {
  customElements.define('product-thumbnail-rail', ProductThumbnailRail);
}
